import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {UNKNOWN_FOLDER} from 'file-browser/FolderSelector';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {
  CanChangeStudyableAssignmentFolderQuery,
  CanChangeStudyableAssignmentFolderQueryVariables,
} from './__generated/CanChangeStudyableAssignmentFolderQuery';
import {
  CanChangeStudyableAssignmentStudiesQuery,
  CanChangeStudyableAssignmentStudiesQueryVariables,
} from './__generated/CanChangeStudyableAssignmentStudiesQuery';

function isFolderInvalid(folderId: string | null | undefined): boolean {
  return folderId == null || folderId === UNKNOWN_FOLDER;
}

export const CAN_CHANGE_STUDYABLE_ASSIGNMENT_FOLDER_QUERY: DocumentNode = gql`
  query CanChangeStudyableAssignmentFolderQuery($folderId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    folder(id: $folderId) {
      ... on FolderNode {
        id
        permissions {
          canChangeStudyableAssignment
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useCanChangeStudyableAssignmentFolderQuery(
  folderId: string | null | undefined
): boolean | null {
  const result = useSkippableBenchlingQuery<
    CanChangeStudyableAssignmentFolderQuery,
    CanChangeStudyableAssignmentFolderQueryVariables
  >(CAN_CHANGE_STUDYABLE_ASSIGNMENT_FOLDER_QUERY, {
    skip: isFolderInvalid(folderId),
    variables: {folderId: folderId!},
  });
  const {type: state} = result;
  switch (state) {
    case QueryResultType.SKIPPED:
      return null;
    case QueryResultType.SUCCESS:
      if (isInaccessible(result.data.folder)) {
        return null;
      }
      return result.data.folder.permissions.canChangeStudyableAssignment;
    default:
      return null;
  }
}

export const CAN_CHANGE_STUDYABLE_ASSIGNMENT_STUDIES_QUERY: DocumentNode = gql`
  query CanChangeStudyableAssignmentStudiesQuery($studyIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: STUDIES) {
    studies(ids: $studyIds) {
      ... on StudyNode {
        id
        name
        permissions {
          canChangeStudyableAssignment
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useCanChangeStudyableAssignmentStudiesQuery(
  studyIds: Array<string>
): Map<string, boolean | null> {
  const result = useSkippableBenchlingQuery<
    CanChangeStudyableAssignmentStudiesQuery,
    CanChangeStudyableAssignmentStudiesQueryVariables
  >(CAN_CHANGE_STUDYABLE_ASSIGNMENT_STUDIES_QUERY, {
    skip: studyIds.length === 0,
    variables: {studyIds},
  });
  const {type: state} = result;
  const studyNamesToStudyablePermission = new Map<string, boolean | null>();
  if (state === QueryResultType.SUCCESS) {
    for (const study of result.data.studies) {
      if (isInaccessible(study)) {
        continue;
      }
      studyNamesToStudyablePermission.set(study.name, study.permissions.canChangeStudyableAssignment);
    }
  }
  return studyNamesToStudyablePermission;
}
